import { b, types } from "@/b";
import { HEADER_FRAGMENT } from "@/components/Header";
import { PageLayout } from "@/components/PageLayout";
import { MEDIA_FRAGMENT, ResponsiveImage } from "@/components/ResponsiveImage";
import { Hero } from "@/components/layout/Hero";
import { Section } from "@/components/layout/Section";
import {
  CartSection,
  CartPreviewSection_Fragment,
} from "@/components/pages/sections/CartSection";
import {
  FilteredProductGrid,
  FilteredProductGrid_Fragment,
} from "@/components/pages/collection/FilteredProductGrid";
import { getApolloSSRProps, useHydratedQuery } from "@/util/apollo-client";
import {
  Hook_IsSubscribedToVIP_Fragment,
  useIsSubscribedToVIP,
} from "@/util/hook";
import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  Text,
  keyframes,
} from "@chakra-ui/react";
import {
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
  NextPage,
} from "next";
import { useRouter } from "next/router";
import { useEffect, useMemo } from "react";
import Head from "next/head";
import { SubscriptionCurrencyRenderer } from "@/components/SubscriptionCurrencyRenderer";
import { ReadyToBecomeVIPSection } from "@/components/pages/sections/ReadyToBecomeVIP";
import { useMutation } from "@apollo/client";
import {
  FilteredProductMenuGrid,
  FilteredProductMenuGrid_Fragment,
} from "@/components/pages/menu/FilteredProductMenuGrid";
import { popupMiddleware } from "@/sanity/popup-middleware";

const MENU_QUERY = b.query("Menu", { clock: "String" }, (b, v) => {
  return [
    //
    b.menu(
      {
        clock: v.clock,
      },
      (b) => [b.__fragment(FilteredProductMenuGrid_Fragment)]
    ),
    b.__fragment(Hook_IsSubscribedToVIP_Fragment),
    b.__fragment(HEADER_FRAGMENT),
    b.__fragment(CartPreviewSection_Fragment),
  ];
});

const MUTATION_ADD_COUPON_TO_CART = b.mutation(
  "AddCouponToCart",
  {
    code: "String!",
  },
  (b, v) => [
    b.viewer((b) => [
      b.cart((b) => [
        b.applyCoupon({ code: v.code }, (b) => [b.totalQuantity()]),
      ]),
    ]),
  ]
);

const Menu: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = (
  props
) => {
  const router = useRouter();
  const { data } = useHydratedQuery(props);
  const clock = props.__APOLLO_STATE_QUERY__.variables.clock;
  const isSubscribedToVIP = useIsSubscribedToVIP(data, true);
  const [applyCoupon] = useMutation(MUTATION_ADD_COUPON_TO_CART.document());
  useEffect(() => {
    if (router.query.coupon) {
      applyCoupon({
        variables: {
          code: String(router.query.coupon),
        },
      }).catch((e) => {
        console.error(e);
      });
    }
  }, [router.query.coupon, applyCoupon]);

  const hero = useMemo(() => {
    if (typeof router.query.coupon === "string") {
      return (
        <Section>
          <Hero
            header="Coupon Applied!"
            tagline="You've successfully applied a coupon to your cart. You can view and modify the discount during checkout. Check out our menu below!"
            buttons={[]}
            textContainer={{
              display: "flex",
              minW: "2xl",
              justifyContent: "center",
              flexDir: "column",
            }}
          />
        </Section>
      );
    }
    if (router.query.new_vip === "show") {
      return (
        <Section>
          <Hero
            header="Preview VIP pricing!"
            tagline="With a VIP subscription in your cart, you can take advantage of our VIP pricing right away!"
            buttons={[
              {
                label: "View Menu",
                onClick: () => {
                  window.scrollTo({
                    top: 250,
                    behavior: "smooth",
                  });
                },
              },
            ]}
          />
        </Section>
      );
    }

    if (isSubscribedToVIP) {
      return (
        <Section>
          <Hero
            header="Check out your savings!"
            tagline="Thanks for being a VIP! All of our meals are discounted for you."
            buttons={[]}
          />
        </Section>
      );
    }

    return (
      <Section>
        <Hero
          header="Want to Save More on Meals?"
          tagline="Order any of our gluten-free, farm-to-table, ready to eat meals & save up to 25% when you become a Visionary VIP!"
          buttons={[
            {
              label: (
                <>
                  Subscribe for only{" "}
                  <SubscriptionCurrencyRenderer
                    data={data.subscription!.price}
                  />
                  !
                </>
              ),
              onClick: () => {
                router.push("/vip");
              },
            },
            {
              label: "View Menu",
              onClick: () => {
                window.scrollTo({
                  top: 400,
                  behavior: "smooth",
                });
              },
            },
          ]}
        />
      </Section>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageLayout data={data}>
      <Head>
        <title>Menu</title>
        <meta
          name="description"
          content="Explore our delicious gluten-free, farm to table, fully-prepped meals!"
        />
      </Head>
      {hero}
      {data.viewer.cart.totalQuantity > 0 && <CartSection data={data} />}
      {clock && Boolean(data.viewer.employee) && (
        <Section>
          <Flex
            flexDir="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            p="4"
            bg="gray.100"
            borderRadius="md"
          >
            <Heading>Admin Clock Change</Heading>
            <Text fontSize="sm" mb="2">
              You are viewing the menu as if it were {clock}.
            </Text>
            <Button
              onClick={() => {
                router.push("/menu");
              }}
            >
              Click to the current menu
            </Button>
          </Flex>
        </Section>
      )}
      <Section padded>
        <Heading as="h1" mb="8" fontSize="4xl">
          Menu
        </Heading>
        <FilteredProductMenuGrid data={data.menu! as any} />
      </Section>
      <ReadyToBecomeVIPSection />
    </PageLayout>
  );
};

export const getServerSideProps = popupMiddleware(async (ctx) => {
  const props = await getApolloSSRProps(
    MENU_QUERY.document(),
    {
      clock: ctx.query.clock ? String(ctx.query.clock) : null,
    },
    ctx
  );

  if (!props.__APOLLO_STATE_QUERY__.result.menu) {
    return {
      notFound: true,
    };
  }

  return {
    props,
  };
});

export default Menu;
